import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { PhoneCall, X, Check, Clock } from "lucide-react";
import { COMPANY } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  "Tout de suite",
  "Dans 15 min",
  "Dans 1 heure",
  "Plus tard aujourd'hui",
  "Demain",
];

export default function CallbackButton() {
  const [open, setOpen] = useState(false);
  const [prenom, setPrenom] = useState("");
  const [telephone, setTelephone] = useState("");
  const [creneau, setCreneau] = useState(SLOTS[0]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-callback", onOpen);
    return () => window.removeEventListener("open-callback", onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (prenom.trim().length < 2 || telephone.trim().length < 6) {
      toast.error("Merci de renseigner votre prénom et votre téléphone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/callback`, {
        prenom: prenom.trim(),
        telephone: telephone.trim(),
        creneau,
      });
      toast.success("Demande envoyée — on vous rappelle au plus vite !");
      setDone(true);
      setTimeout(() => {
        setOpen(false);
        setDone(false);
        setPrenom("");
        setTelephone("");
        setCreneau(SLOTS[0]);
      }, 2200);
    } catch (err) {
      toast.error("Erreur d'envoi, merci d'appeler directement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <button
        data-testid="callback-fab"
        onClick={() => setOpen(true)}
        className="hidden sm:inline-flex fixed bottom-6 right-6 z-40 items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-full pl-4 pr-5 py-3 shadow-2xl shadow-teal-900/40 transition-all hover:-translate-y-0.5"
      >
        <span className="w-9 h-9 rounded-full bg-white text-teal-700 flex items-center justify-center -ml-1">
          <PhoneCall className="w-4 h-4" />
        </span>
        <span className="leading-tight text-left">
          <span className="block text-[10px] uppercase tracking-widest font-bold text-teal-100">
            Gratuit · 2 min
          </span>
          <span className="block text-sm">Me faire rappeler</span>
        </span>
      </button>

      {open && (
        <div
          data-testid="callback-modal"
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
        >
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
            <button
              data-testid="callback-close"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-7 md:p-8">
              <div className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-3">
                <Clock className="w-3 h-3" /> Rappel gratuit · 2 min
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 leading-tight">
                On vous rappelle.
              </h2>
              <p className="text-slate-600 text-sm mt-2">
                Laissez-nous votre prénom et votre numéro, nous vous rappelons
                dans le créneau choisi.
              </p>

              {done ? (
                <div
                  data-testid="callback-success"
                  className="mt-6 p-5 rounded-2xl bg-teal-50 border border-teal-200 text-teal-900"
                >
                  <div className="flex items-center gap-2 font-bold">
                    <Check className="w-5 h-5" /> Merci {prenom || ""} !
                  </div>
                  <div className="text-sm mt-1">
                    On vous rappelle très rapidement.
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
                  <label className="block">
                    <span className="block text-xs font-bold text-slate-700 mb-1">
                      Prénom
                    </span>
                    <input
                      data-testid="callback-input-prenom"
                      type="text"
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      placeholder="Jean"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 outline-none focus:border-teal-500 focus:bg-white"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs font-bold text-slate-700 mb-1">
                      Téléphone
                    </span>
                    <input
                      data-testid="callback-input-telephone"
                      type="tel"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      placeholder="06 12 34 56 78"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 outline-none focus:border-teal-500 focus:bg-white"
                    />
                  </label>
                  <div>
                    <span className="block text-xs font-bold text-slate-700 mb-2">
                      Quand on vous rappelle ?
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {SLOTS.map((s) => (
                        <button
                          key={s}
                          type="button"
                          data-testid={`callback-slot-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          onClick={() => setCreneau(s)}
                          className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition-colors ${
                            creneau === s
                              ? "bg-teal-600 text-white border-teal-600"
                              : "bg-white text-slate-700 border-slate-200 hover:border-teal-500"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="callback-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-bold rounded-full px-6 py-4 mt-2 shadow-lg shadow-orange-600/30"
                  >
                    {loading ? "Envoi..." : "Me faire rappeler"}
                  </button>
                  <p className="text-[11px] text-slate-500 text-center">
                    Ou appelez directement au{" "}
                    <a
                      href={`tel:${COMPANY.phoneDigits}`}
                      className="font-bold text-teal-700"
                    >
                      {COMPANY.phone}
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
